import {
  ACTIVITY_TRAIL_THINKING_ID,
  LONG_WAIT_AFTER_MS,
  THINKING_BUDGET_MS,
  thinkingRowId,
  type TimelineRow,
  type TrailBucket,
  type TrailStepRecord,
} from '@/components/assistant-ui/thread/trail-records';
import type { StepTimingsMap } from '@/components/assistant-ui/thread/trail-timing';
import { pickInSequence } from '@/lib/utils/pick-by-hash';

/** Per-row hard ceiling on a single thinking-row's displayed duration — defends against stale-anchor render bugs producing days-long rows. */
const MAX_SINGLE_THINKING_MS = 5 * 60 * 1000;

export interface TrailTimelineLabels {
  /**
   * Initial "thinking…" label. Pushed unconditionally as the first row so
   * users always see immediate progress (a live ticking row) regardless of
   * how fast the response arrives.
   */
  thinking: string;
  /** Generic gap-row variants. Bucket-agnostic by design — see TrailVariantOverrides. */
  preparing: readonly string[];
  /** Long-wait variants used past `longWaitAfterMs`. Bucket-agnostic. */
  longWait: readonly string[];
  /**
   * When provided AND `isMessageRunning` is false, replaces the last text
   * row's label (or appends a separate receipt for tool-only runs) with
   * this getter's output for the run's last completed bucketed tool. The
   * closer is the only bucket-aware copy in the trail — it summarizes work
   * that already happened, so context is honest there.
   */
  pickFinal?: (bucket: TrailBucket) => string | undefined;
}

export interface BuildTrailTimelineParams {
  stepRecords: readonly TrailStepRecord[];
  stepTimings: StepTimingsMap;
  /** Run start anchor (epoch ms). */
  messageStartAt: number;
  /** Current "now" of the frame: `tickNow` while live, `start + totalDurationMs` while done. */
  messageEndAt: number;
  isMessageRunning: boolean;
  labels: TrailTimelineLabels;
  longWaitAfterMs?: number;
  /**
   * When a frontend tool is paused for user input (approval), the in-progress tool step is
   * the loader — don't append a trailing "thinking" row (it would steal the spinner and
   * mislead with "…thinking" copy while we're actually waiting on the user).
   */
  suppressTrailingRow?: boolean;
  /**
   * Keep the always-on initial "thinking…" row `completed` even while running. Used after a HITL
   * tool resolves and the run auto-continues: the initial thinking already happened, and the only
   * step record (a client tool) has no `startedAt`, which would otherwise re-flip the first row to
   * a spinner. The continuation's live state is shown by the trailing row instead.
   */
  keepInitialThinkingComplete?: boolean;
  /**
   * The live trailing loader the last frame showed. It stays however short the gap reads: an anchor
   * that jumps to the newest instant reads near zero until the clock ticks, and dropping the row for
   * that tick would remount it, sliding it in again and shifting everything below.
   */
  shownTrailingRowId?: string;
  /** The guest stopped this run: the steps it cut off read as cancelled, and no final row says the answer is ready. */
  stopped?: boolean;
  /**
   * The browser is doing a paused run's work (a client-side tool). The run is not streaming, but a
   * step still in progress is genuinely live, so it keeps its loader instead of reading as done.
   */
  clientWorkInFlight?: boolean;
}

export interface TrailTimeline {
  rows: TimelineRow[];
  totalMs: number;
}

/**
 * Fallback `stepEnd` for a row whose timing has no `completedAt`. While
 * running: in-flight rows tick via `safeEnd`; completed rows that never
 * received a timing close keep `startedAt` so the trailing thinking row
 * absorbs the rest. After finish: collapse to `safeEnd` so a mid-text-delta
 * cut doesn't render `durationMs=0` forever.
 */
function resolveStepEnd({
  completedAt,
  isMessageRunning,
  stepIsLive,
  safeEnd,
  startedAt,
}: {
  completedAt: number | undefined;
  isMessageRunning: boolean;
  stepIsLive: boolean;
  safeEnd: number;
  startedAt: number;
}): number {
  if (completedAt !== undefined) return completedAt;
  if (!isMessageRunning) return safeEnd;
  return stepIsLive ? safeEnd : startedAt;
}

/**
 * Builds the rendered row list from `stepRecords` + their timings.
 *
 * Emits, in chronological order: brand header row, one thinking-gap row per
 * inter-event gap, one row per real step. Post-process pass ensures at most
 * ONE row carries `in-progress` while running (the latest in-flight one) so
 * the trail always feels like work is happening on a single visible row.
 */
/**
 * After a reload, rehydrated/replayed tool parts can arrive without results for a
 * beat, classifying several records as 'in-progress' at once — with no observed
 * timings (the live mirror died with the previous page) every one of them would
 * spin. Steps run in order, so an unresolved step that has records after it
 * already finished; only the LATEST unresolved step is genuinely live. Records
 * WITH an observed `startedAt` are trusted as-is — that's what keeps live
 * parallel tool batches ticking independently.
 */
function demoteStaleInProgress({
  stepRecords,
  stepTimings,
}: {
  stepRecords: readonly TrailStepRecord[];
  stepTimings: StepTimingsMap;
}): readonly TrailStepRecord[] {
  const lastIndex = stepRecords.length - 1;
  const isStale = (record: TrailStepRecord, i: number) =>
    i < lastIndex &&
    record.status === 'in-progress' &&
    !record.appearing &&
    stepTimings[record.id] === undefined;
  if (!stepRecords.some(isStale)) return stepRecords;
  return stepRecords.map((record, i) =>
    isStale(record, i) ? { ...record, status: 'completed' as const } : record,
  );
}

export function buildTrailTimeline({
  stepRecords: rawStepRecords,
  stepTimings,
  messageStartAt,
  messageEndAt,
  isMessageRunning,
  labels,
  longWaitAfterMs = LONG_WAIT_AFTER_MS,
  suppressTrailingRow = false,
  keepInitialThinkingComplete = false,
  shownTrailingRowId,
  stopped = false,
  clientWorkInFlight = false,
}: BuildTrailTimelineParams): TrailTimeline {
  const stepsMayBeLive = isMessageRunning || clientWorkInFlight;
  const stepRecords = demoteStaleInProgress({ stepRecords: rawStepRecords, stepTimings });
  const { thinking: thinkingLabel, preparing, longWait, pickFinal } = labels;
  // `startedAt` is internal — only the post-process pass needs it; stripped
  // from the public TimelineRow before return.
  type InternalRow = TimelineRow & { startedAt: number };
  const rows: InternalRow[] = [];
  const safeEnd = Math.max(messageStartAt, messageEndAt);
  const totalMs = Math.max(0, safeEnd - messageStartAt);

  let cursor = messageStartAt;
  let gapIndex = 0;
  const wantsContent = isMessageRunning || stepRecords.length > 0;

  // Mirrors the loop's `startedAt` fallback so we can compute the first-gap
  // duration before entering the loop — drives both the thinking row's
  // timing and the cursor advance below.
  const firstStepStartedAt = ((): number => {
    if (stepRecords.length === 0) return safeEnd;
    const first = stepRecords[0];
    const observed = stepTimings[first.id]?.startedAt;
    if (observed !== undefined) return observed;
    const stepIsLive = first.status === 'in-progress' && isMessageRunning;
    return stepIsLive ? safeEnd : messageStartAt;
  })();
  const firstGapMs = Math.min(
    Math.max(0, firstStepStartedAt - messageStartAt),
    MAX_SINGLE_THINKING_MS,
  );
  const thinkingSubMs = Math.min(firstGapMs, THINKING_BUDGET_MS);
  // Thinking row keeps its spinner while: still running, hasn't capped at the
  // budget, and no real first step has arrived. Record existence — not timing
  // presence — is the signal: after a reload the timings sidecar is gone but
  // rehydrated/replayed records prove steps already happened, and the thinking
  // spinner must not run alongside their loaders.
  const noStepHasArrived = stepRecords.length === 0;
  const thinkingIsLive =
    !keepInitialThinkingComplete &&
    isMessageRunning &&
    thinkingSubMs < THINKING_BUDGET_MS &&
    noStepHasArrived;

  // Always-on initial "thinking…" row — first row in the trail. Tracks the
  // first-gap duration capped at `THINKING_BUDGET_MS`. Once capped, the loop's
  // normal first-gap emission picks up the remainder with the rotating
  // preparing variants. We set `liveAnchor` to `messageStartAt` while live so
  // the debug counter ticks smoothly from 0; once capped or the first step
  // arrives, we drop `liveAnchor` and freeze on `durationMs` to honour the cap.
  if (wantsContent && thinkingLabel) {
    rows.push({
      id: ACTIVITY_TRAIL_THINKING_ID,
      kind: 'thinking',
      label: thinkingLabel,
      status: thinkingIsLive ? 'in-progress' : 'completed',
      durationMs: thinkingSubMs,
      liveAnchor: thinkingIsLive ? messageStartAt : undefined,
      isInFlight: false,
      startedAt: messageStartAt,
    });
    cursor = messageStartAt + thinkingSubMs;
  }

  const pushGapRow = ({
    label,
    durationMs,
    startedAt,
  }: {
    label: string;
    durationMs: number;
    startedAt: number;
  }) => {
    rows.push({
      id: thinkingRowId(gapIndex),
      kind: 'thinking',
      label,
      status: 'completed',
      durationMs,
      liveAnchor: undefined,
      // Mid-stream thinking rows are always closed by the next event; only
      // the trailing-gap pass below promotes one to in-flight.
      isInFlight: false,
      startedAt,
    });
    gapIndex++;
  };

  // One-time label swap at `longWaitAfterMs` — "preparing" → "deeper thinking".
  // We don't keep rotating: the user should feel the model is digging in, not
  // cycling through scripts. `gapIndex` is the walk index so consecutive gap
  // rows in the same run get distinct variants.
  //
  // Seed is state-aware (records count + last record's kind) on top of the
  // walk index. Without that, a parallel batch of N tools (advancing
  // gapIndex by N) lines up with N variants and the trailing thinking row
  // re-uses the same pick. Mixing record-state into the seed scrambles the
  // hash so consecutive trailing rows differ in practice.
  const pickGapLabel = (chunkMs: number): string => {
    const useDeeper = chunkMs >= longWaitAfterMs && longWait.length > 0;
    const variants = useDeeper ? longWait : preparing;
    if (variants.length === 0) return thinkingLabel;
    const lastRec = stepRecords[stepRecords.length - 1];
    const sig = lastRec ? `${stepRecords.length}:${lastRec.kind}` : 'init';
    return pickInSequence({ items: variants, seed: `gap:${sig}`, index: gapIndex });
  };

  const emitThinkingRowForGap = ({ gapStart, gapEnd }: { gapStart: number; gapEnd: number }) => {
    const rawThinkingMs = Math.max(0, gapEnd - gapStart);
    const thinkingMs = Math.min(rawThinkingMs, MAX_SINGLE_THINKING_MS);
    pushGapRow({
      label: pickGapLabel(thinkingMs),
      durationMs: thinkingMs,
      startedAt: gapStart,
    });
  };

  // Track the most-recent completed bucketed tool for the final closer —
  // the closer is the only bucket-aware copy in the trail (intermediate
  // rows are bucket-agnostic). `cold` until a tool with a bucket completes.
  let lastToolBucket: TrailBucket = 'cold';
  let anyInFlight = false;
  // Steps still shown as active whose duration is already final (an answer still appearing after
  // its timing closed): they keep the spinner but show the recorded duration, not a live timer.
  const finalDurationIds = new Set<string>();
  for (const record of stepRecords) {
    const timing = stepTimings[record.id];
    const stepIsLive = record.status === 'in-progress' && stepsMayBeLive;
    // For an in-progress step without an observed timing, use `safeEnd` as
    // the synthetic startedAt so the leading gap chunks the user has been
    // watching don't collapse on the render the step first appears in.
    const fallbackStartedAt = stepIsLive ? safeEnd : cursor;
    const startedAt = timing?.startedAt ?? fallbackStartedAt;
    const completedAt = timing?.completedAt;

    // Every record claims the next `gap:N` slot — used as the React key, so
    // a record landing in a slot the trailing thinking row occupied last
    // frame shares the same instance and morphs in place via the icon
    // crossfade. Bumping `gapIndex` unconditionally also keeps
    // preparing-variant rotation honest.
    const isTextRecord = record.kind === 'text';
    const slotMorphKey = thinkingRowId(gapIndex);
    gapIndex++;

    const stepEnd = resolveStepEnd({
      completedAt,
      isMessageRunning,
      stepIsLive,
      safeEnd,
      startedAt,
    });
    // Text rows absorb the leading gap into their own duration (preparing →
    // streaming reads as one phase). Tool rows keep their own `startedAt`
    // so the debug timer reflects execution time, not the wait that
    // preceded it. The MAX cap defends against stale anchors.
    const rowStartedAt = isTextRecord
      ? Math.max(Math.min(cursor, startedAt), startedAt - MAX_SINGLE_THINKING_MS)
      : startedAt;
    const rowLabel =
      isTextRecord && record.awaitsContent ? pickGapLabel(stepEnd - rowStartedAt) : record.label;
    rows.push({
      id: record.id,
      kind: record.kind,
      label: rowLabel,
      status: record.status,
      waitsFor: record.waitsFor,
      durationMs: Math.max(0, stepEnd - rowStartedAt),
      liveAnchor: undefined,
      // Once `completedAt` exists the row's duration is fixed — promoting it
      // back to in-progress would let live elapsed drift past the persisted value.
      isInFlight: (completedAt === undefined || !!record.appearing) && stepIsLive,
      startedAt: rowStartedAt,
      morphKey: slotMorphKey,
    });

    if (record.appearing && completedAt !== undefined) finalDurationIds.add(record.id);

    // Parallel batches: a later record can complete before an earlier one.
    // Clamp so cursor never moves backwards and produces a phantom gap.
    cursor = Math.max(cursor, stepEnd);

    if (record.kind === 'tool' && record.status === 'completed' && record.toolBucket) {
      lastToolBucket = record.toolBucket;
    }
    if (
      isMessageRunning &&
      record.status === 'in-progress' &&
      (stepTimings[record.id]?.completedAt === undefined || record.appearing)
    ) {
      anyInFlight = true;
    }
  }

  // Trailing thinking row — only emitted when *no* record is currently
  // in-flight. If any tool/text is running (including any tool in a
  // parallel batch where the LAST record happens to have completed first),
  // those rows are the active loaders and a trailing thinking would steal
  // their slot.
  // 50 ms threshold suppresses the storybook's "brand-only frame" where
  // messageEndAt === messageStartAt.
  const TRAILING_MIN_GAP_MS = 50;
  const trailingGapMs = Math.max(0, safeEnd - cursor);
  const trailingRowDue =
    isMessageRunning &&
    (trailingGapMs >= TRAILING_MIN_GAP_MS || thinkingRowId(gapIndex) === shownTrailingRowId);

  // Trailing row morphs across running → finished: same id swaps from
  // in-flight loader to ✓ + finalLabel without an unmount-flash. The
  // empty-records case falls back to brand-split (gap:0/gap:1) instead so
  // the first thinking phase still has the brand-text-then-preparing morph.
  // A step still waiting on the user means the answer is not ready, whatever the stream says.
  const awaitingUser = stepRecords.some(record => record.status === 'awaiting-approval');
  if (!anyInFlight && !suppressTrailingRow && !awaitingUser) {
    const hasRecords = stepRecords.length > 0;
    if (!hasRecords && trailingRowDue) {
      // No real records yet — fall back to brand-split (gap:0/gap:1) so the
      // first thinking phase has the brand-text-then-preparing morph.
      const beforeEmit = rows.length;
      emitThinkingRowForGap({ gapStart: cursor, gapEnd: Math.max(cursor, safeEnd) });
      if (rows.length > beforeEmit) rows[rows.length - 1].isInFlight = true;
    } else if (hasRecords) {
      const showWhileRunning = trailingRowDue;
      // Resolve `finalLabel` only when the trail is actually finished —
      // skips the resolver (and its records-walks) entirely on every
      // streaming tick. Always emitted as its own row (including after text
      // rows) so the post-stream wait between the last record completing and
      // the message marking complete is visibly accounted for.
      const finalLabel = !isMessageRunning && !stopped ? pickFinal?.(lastToolBucket) : undefined;
      const showAfterFinish = !isMessageRunning && !!finalLabel;
      if (showWhileRunning || showAfterFinish) {
        const cappedMs = Math.min(trailingGapMs, MAX_SINGLE_THINKING_MS);
        // Reuse the next gap:N id so the in-loop gap that fills this slot
        // next render keeps the same React instance for spinner→check
        // crossfade. Pick the label BEFORE incrementing gapIndex so the
        // pickInSequence seed (`gap:${gapIndex}`) matches the id.
        const trailingId = thinkingRowId(gapIndex);
        const trailingLabel = showWhileRunning ? pickGapLabel(cappedMs) : (finalLabel as string);
        gapIndex++;
        rows.push({
          id: trailingId,
          kind: 'thinking',
          label: trailingLabel,
          status: 'completed',
          durationMs: cappedMs,
          liveAnchor: undefined,
          isInFlight: showWhileRunning,
          startedAt: cursor,
        });
      }
    }
  }

  // Every still-in-flight row ticks independently — necessary for parallel
  // batches where the LATEST in-flight row isn't the only loader. Rows with a
  // known `completedAt` stay frozen so displayed elapsed never drifts past
  // what `step_timings` will report after finalize. The thinking row owns its
  // status/durationMs (capped at `THINKING_BUDGET_MS`) so we skip it here —
  // letting the generic flip would overwrite its 'in-progress' to 'completed'
  // or attach a runaway liveAnchor that ticks past the cap.
  if (rows.length > 0) {
    for (let i = 0; i < rows.length; i++) {
      // Terminal/held states carry their own meaning — don't flip them to 'completed'.
      // 'awaiting-approval' is a held state (the user's turn), preserved like the terminals so
      // the your-turn glyph survives normalization.
      if (
        rows[i].status === 'failed' ||
        rows[i].status === 'cancelled' ||
        rows[i].status === 'awaiting-approval'
      )
        continue;
      if (rows[i].id === ACTIVITY_TRAIL_THINKING_ID) continue;
      if (stepsMayBeLive && rows[i].isInFlight) {
        rows[i].status = 'in-progress';
        rows[i].liveAnchor = finalDurationIds.has(rows[i].id) ? undefined : rows[i].startedAt;
      } else {
        rows[i].status = stopped && rows[i].status === 'in-progress' ? 'cancelled' : 'completed';
      }
    }
  }

  const out: TimelineRow[] = rows.map(({ startedAt: _startedAt, ...row }) => row);

  return { rows: out, totalMs };
}
